import { Location } from '@angular/common';
import { ApplicationRef, effect, inject, Injectable, signal } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { firstValueFrom, Observable, of } from 'rxjs';

import { ENVIRONMENT } from '@environment';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { CountryService } from '@usheru-hff/sdk/country';
import { LocationService, ushCountryVersionKey } from '@usheru-hff/sdk/location';
import { Country } from '@usheru-hff/shared/domain';
import { COUNTRY_VERSION_TOKEN } from '@usheru-hff/shared/tokens';

@Injectable({
  providedIn: 'root'
})
export class CountrySelectionService {
  private readonly countryService = inject(CountryService);
  private readonly cookieService = inject(CookieService);
  private readonly locationService = inject(LocationService);

  private readonly channelService = inject(ChannelService);
  private readonly environment = inject(ENVIRONMENT);
  /**
   * COUNTRY_VERSION_TOKEN is setup on `server.ts` during SSR.
   **/
  private readonly countryVersionFromToken = inject(COUNTRY_VERSION_TOKEN, { optional: true });

  private readonly countryVersionFromCookie = this.cookieService.get(ushCountryVersionKey);
  private readonly defaultCountryVersion = this.environment.countryBasedApplication?.default.countryCode;

  public readonly initialCountryVersionCode = this.resolveCountryVersion();
  public readonly initialCountry$ = this.resolveInitialCountry();
  public readonly countrySelected = signal<Country | undefined>(undefined);

  /**
   * Take the language from:
   * 1 - Cookie (previously selected if SSR not enabled or failed)
   * 2 - SSR Token (previously selected)
   * 3 - If none, take the default country configuration from enviroment.
   *  */
  private resolveCountryVersion(): string | undefined {
    //console.info(`1 - Country version from cookie: ${this.countryVersionFromCookie}`);
    //console.info(`2 - Country version from token: ${this.countryVersionFromToken}`);
    //console.info(`3- Default country version: ${this.defaultCountryVersion}`);

    return this.countryVersionFromCookie || this.countryVersionFromToken || this.defaultCountryVersion;
  }

  /**
   * Resolve the initial country by:
   * 1 - Cookie (previously selected if SSR not enabled or failed)
   * 2 - SSR Token (previously selected)
   * 3 - If none, take the default country configuration from enviroment.
   */
  private resolveInitialCountry(): Observable<Country | undefined> {
    return this.initialCountryVersionCode
      ? this.countryService.getCountryByCode2(this.initialCountryVersionCode)
      : of(undefined);
  }

  readonly FAVORITE_LANG_TOKEN = 'favorite_lang';
  private readonly USH_COUNTRY_TOKEN = 'ush_country_version';

  constructor(
    private location: Location,
    private applicationRef: ApplicationRef
  ) {
    effect(
      () => {
        const countrySelected = this.countrySelected();

        // console.info('Country selected:', countrySelected);
        if (!countrySelected) {
          return;
        }
        this.blockScreen();
        const countryCode = countrySelected.code2.toLowerCase();
        const languageCode = this.channelService
          .getChannelLanguageFromCountryCode(countrySelected.code3)
          .code?.toLowerCase();
        // console.info(`For country ${countryCode}, got language ${languageCode}`);
        const defaultLocation = this.environment.countryBasedApplication.defaultLocations[countryCode];
        // console.info('default location: ', defaultLocation);

        const location = this.locationService.getCoordsFromPointLocation(defaultLocation);
        // Clean-up location loca
        this.locationService.cleanLocationStorage();
        this.locationService.setLocationApi(location);

        firstValueFrom(this.locationService.$locationChanged.asObservable()).then(() => {
          this.updateCookie(this.USH_COUNTRY_TOKEN, countryCode);

          if (languageCode) {
            // console.log(`Updating language cookie to ${languageCode}`);
            this.updateCookie(this.FAVORITE_LANG_TOKEN, languageCode);
            this.navigateToNewLangUrl(languageCode);
          }
        });
      },
      { allowSignalWrites: true }
    );
  }

  private navigateToNewLangUrl(langCode: string) {
    const baseUrl = this.location.path(true);
    const urlParts = baseUrl.split('/'); // Split by slashes
    urlParts[1] = langCode; // Replace language segment
    this.modifyUrl(langCode);
    this.applicationRef.tick();

    window.location.reload();
  }

  private modifyUrl(langCode: string) {
    const currentUrl = window.location.href;
    const newUrl = currentUrl.replace(/\/[a-z]{2}\//, `/${langCode}/`);
    window.history.pushState({}, '', newUrl);
  }

  private updateCookie(key: string, value: string) {
    if (this.cookieService.check(key)) {
      this.cookieService.delete(key);
    }
    this.cookieService.set(key, value, 100, '/');
  }

  /**
   * Block the screen with a spinner while the page is reloading in {@link navigateToNewLangUrl}.
   * This will remove with the page reload.
   *
   * NOTE: This is not used because was not requested but can be used in the future.
   */
  private blockScreen() {
    const div = document.createElement('div');
    // BACKGROUND
    div.classList.add('block-screen');
    div.style.position = 'fixed';
    div.style.top = '0';
    div.style.left = '0';
    div.style.width = '100vw';
    div.style.height = '100vh';
    div.style.backgroundColor = 'hsla(0, 0.00%, 0.00%, 0.30)';
    div.style.zIndex = '9999';
    // SPINER
    const spiner = document.createElement('div');
    spiner.style.position = 'absolute';
    spiner.style.top = '50%';
    spiner.style.left = '50%';
    spiner.style.transform = 'translate(-50%, -50%)';
    spiner.style.width = '50px';
    spiner.style.height = '50px';
    spiner.style.border = '5px solid #f3f3f3';
    spiner.style.borderTop = '5px solid var(--brandColor)';
    spiner.style.borderRadius = '50%';
    spiner.style.animation = 'p-icon-spin 2s infinite linear';
    // APPEND
    div.appendChild(spiner);
    document.body.appendChild(div);
    // UNFOCUS CURSOR
    const children = document.body.children;
    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement;
      if (!child.classList.contains('block-screen')) {
        child.style.pointerEvents = 'none';
      }
    }
  }
}
